import { FC, PropsWithChildren, ReactNode, useContext, useMemo } from 'react';
import { Options } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import {
  Dictionary,
  DictionaryContext,
  FElementEmbeddedInformationModal,
  useApplyDictionaryToDocument
} from '@belong/contentful';
import { getLangFromText } from '@belong/type-utils';
import { IButton, IRichTextProps, TEXT_NODES, TNodeKeys, TNODES } from '@belong/types';
import { FeedbackCapture, FFeedbackCapture } from '../../../feedback/FeedbackCapture';
import { EmbeddedInformationModal } from '../../../feedback/Modal/EmbeddedInformationModal';
import ContentFormatter from '../../ContentFormatter';
import BaseRichText from '../BaseRichText/BaseRichText';
import { Copy } from '../Copy';
import { Heading } from '../Heading';
import {
  countryList,
  elementLink,
  elementSystemIcon,
  elementTable,
  moleculeAlert,
  moleculeCallout,
  moleculeCountdownTimer,
  moleculeInformationCalloutRow,
  moleculeLinkList,
  moleculeMediaCaptionBlock,
  sectionAccordionList
} from './NodeRenderers/Entries';
import listRenderers from './NodeRenderers/Lists';
import { markRenderers } from './NodeRenderers/Marks';
import tableRenderers from './NodeRenderers/Tables';
import { RENDER_TEXT, warn } from './NodeRenderers/utils';

const getTextContent = (children: ReactNode): string => {
  if (children === null || children === undefined) {
    return '';
  }

  if (typeof children === 'string' || typeof children === 'number') {
    return String(children);
  }

  if (Array.isArray(children)) {
    return children.map(getTextContent).join('');
  }

  if (typeof children === 'object' && 'props' in children && children?.props) {
    return getTextContent((children as any).props.children);
  }

  return '';
};

export const NODES: TNODES = {
  headingXXLarge(props) {
    return <Heading variant="xxlarge" {...props} tabIndex={-1} />;
  },
  headingXLarge(props) {
    return <Heading variant="xlarge" {...props} tabIndex={-1} />;
  },
  headingLarge(props) {
    return <Heading variant="large" {...props} tabIndex={-1} />;
  },
  headingMedium(props) {
    return <Heading variant="medium" {...props} tabIndex={-1} />;
  },
  headingSmall(props) {
    return <Heading variant="small" {...props} tabIndex={-1} />;
  },
  headingXSmall(props) {
    return <Heading variant="xsmall" {...props} />;
  },
  copyLarge(props) {
    return <Copy variant="large" {...props} />;
  },
  copyMedium(props) {
    return <Copy variant="medium" {...props} />;
  },
  copySmall(props) {
    return <Copy variant="small" {...props} />;
  },
  hr() {
    return <hr aria-hidden="true" />;
  },
  countryList: ({ data }) => countryList(data.target),
  elementEmbeddedInformationModal(props: any, buttonProps: Partial<IButton> = { isLightColor: false }) {
    const { fields } = props.data.target;
    const elementProps = FElementEmbeddedInformationModal({ fields, buttonProps });
    return <EmbeddedInformationModal {...elementProps} />;
  },
  elementLink: ({ data }) => elementLink(data.target),
  elementTable: ({ data }) => elementTable(data.target, RichTextBase),
  elementSystemIcon: ({ data }) => elementSystemIcon(data.target),
  moleculeAlert: ({ data }) => moleculeAlert(data.target, RichTextBase),
  moleculeCallout: ({ data }) => moleculeCallout(data.target),
  moleculeCountdownTimer: ({ data }) => moleculeCountdownTimer(data.target),
  moleculeInformationCalloutRow: ({ data }) => moleculeInformationCalloutRow(data.target),
  moleculeLinkList: ({ data }) => moleculeLinkList(data.target),
  moleculeMediaCaptionBlock: ({ data }) => moleculeMediaCaptionBlock(data.target),
  organismFeedbackCapture: ({ data }) => <FeedbackCapture {...FFeedbackCapture(data.target)} />,
  sectionAccordionList: ({ data }) => sectionAccordionList(data.target, RichTextBase)
};

export const registerRichTextElements = (newElements: Partial<TNODES>): void => {
  Object.assign(NODES, newElements);
};

export const RENDER_NODE = (key: TNodeKeys, props: PropsWithChildren<any>): ReactNode => {
  const nonEmptyChildren = props.children?.filter?.(child => !!child?.[0] || child?.[0] !== '') ?? [];
  const hasNonEmptyChildren = nonEmptyChildren.length > 0;
  return hasNonEmptyChildren && NODES[key](props);
};

/**
 * Renders the rich text document with the given colors and styles applied.
 * Looks up dictionary values in context to apply to string interpolations.
 *
 * @see libs/providers/dictionary/provider.tsx
 * @see libs/providers/dictionary/hooks.tsx
 *
 * If you'd like to render a specific React component in place of the standard
 * ones supplied here, you can do like so:
 *
 * const customParagraphRenderer = (_, children) => {
 *   return (
 *     <MyCustomComponentInsteadOfNative_P_Element className={'gday-cobba'}>
 *       {children}
 *     </MyCustomComponentInsteadOfNative_P_Element>
 *   )
 * }
 *
 * <RichTextBase
 *    renderNode={{ [BLOCKS.PARAGRAPH]: customParagraphRenderer }}
 *    {...otherProps}
 * />
 */
export const RichTextBase: FC<Omit<IRichTextProps, 'dictionary'>> = ({
  html,
  hasColor,
  h1 = TEXT_NODES.headingXXLarge,
  h2 = TEXT_NODES.headingXLarge,
  h3 = TEXT_NODES.headingLarge,
  h4 = TEXT_NODES.headingMedium,
  h5 = TEXT_NODES.headingSmall,
  h6 = TEXT_NODES.headingXSmall,
  p = TEXT_NODES.copyMedium,
  renderNode,
  renderMark,
  renderText = RENDER_TEXT,
  renderBlockEntries = {},
  renderEmbeddedInlines = {},
  headingId,
  headingHasVisibleFocus = false
}: IRichTextProps) => {
  const applyDictionaryToDocument = useApplyDictionaryToDocument();
  const dictionary = useContext(DictionaryContext);

  // Define available renderers for each document block type.
  // Headings 1-6 and Paragraph elements can have their renderer overridden via props
  const options: Options = useMemo(
    () => ({
      renderNode: {
        [BLOCKS.HEADING_1]: (_, children) =>
          RENDER_NODE(h1, {
            children,
            hasColor,
            as: 'h1',
            id: headingId,
            hasVisibleFocus: headingHasVisibleFocus,
            tabIndex: -1
          }),
        [BLOCKS.HEADING_2]: (_, children) =>
          RENDER_NODE(h2, { children, hasColor, as: 'h2', id: headingId, hasVisibleFocus: headingHasVisibleFocus }),
        [BLOCKS.HEADING_3]: (_, children) =>
          RENDER_NODE(h3, { children, hasColor, as: 'h3', id: headingId, hasVisibleFocus: headingHasVisibleFocus }),
        [BLOCKS.HEADING_4]: (_, children) =>
          RENDER_NODE(h4, { children, hasColor, as: 'h4', id: headingId, hasVisibleFocus: headingHasVisibleFocus }),
        [BLOCKS.HEADING_5]: (_, children) =>
          RENDER_NODE(h5, { children, hasColor, as: 'h5', id: headingId, hasVisibleFocus: headingHasVisibleFocus }),
        [BLOCKS.HEADING_6]: (_, children) =>
          RENDER_NODE(h6, { children, hasColor, as: 'h6', id: headingId, hasVisibleFocus: headingHasVisibleFocus }),
        [BLOCKS.HR]: _ => NODES.hr({}),
        [BLOCKS.PARAGRAPH]: (_, children) =>
          RENDER_NODE(p, {
            children,
            hasColor,
            as: 'p',
            lang: getLangFromText(getTextContent(children))
          }),

        ...listRenderers,
        ...tableRenderers,

        /* must be an known contentType */
        [INLINES.EMBEDDED_ENTRY]: ({ data }) => {
          const { id } = data.target.sys.contentType.sys;

          const RENDERABLE_NODES = {
            ...NODES,
            ...renderEmbeddedInlines
          };

          return RENDERABLE_NODES[id]
            ? RENDERABLE_NODES[id]({
                data,
                hasColor,
                serviceId: dictionary?.serviceId,
                orderId: dictionary?.orderId,
                outageId: dictionary?.outageId,
                redirect: dictionary?.redirect,
                testId: dictionary?.testId
              })
            : warn('INLINES.EMBEDDED_ENTRY', data);
        },
        [BLOCKS.EMBEDDED_ENTRY]: ({ data }) => {
          const { id } = data.target.sys.contentType.sys;

          const RENDERABLE_NODES = {
            ...NODES,
            ...renderBlockEntries
          };
          return RENDERABLE_NODES[id] ? RENDERABLE_NODES[id]({ data }) : warn('BLOCKS.EMBEDDED_ENTRY', data);
        },

        // Hyperlinks and Assets are NOT rendered by default
        // use `Element > Link` for hyperlinks
        // use `Molecule > Media Caption Block` for images/videos
        [INLINES.HYPERLINK]: ({ data }) => warn('INLINES.HYPERLINK', data),
        [INLINES.ENTRY_HYPERLINK]: ({ data }) => warn('INLINES.ENTRY_HYPERLINK', data),
        [INLINES.ASSET_HYPERLINK]: ({ data }) => warn('INLINES.ASSET_HYPERLINK', data),
        [BLOCKS.EMBEDDED_ASSET]: ({ data }) => warn('BLOCKS.EMBEDDED_ASSET', data),

        // optionally override renderers via props
        ...renderNode
      },
      renderMark: {
        ...markRenderers,
        ...renderMark
      },
      renderText
    }),
    [
      dictionary?.orderId,
      dictionary?.outageId,
      dictionary?.redirect,
      dictionary?.serviceId,
      dictionary?.testId,
      h1,
      h2,
      h3,
      h4,
      h5,
      h6,
      hasColor,
      headingId,
      headingHasVisibleFocus,
      p,
      renderBlockEntries,
      renderEmbeddedInlines,
      renderMark,
      renderNode,
      renderText
    ]
  );

  if (!html) {
    return null;
  }

  const document = applyDictionaryToDocument(html);

  return <BaseRichText options={options}>{document}</BaseRichText>;
};

RichTextBase.displayName = 'RichTextBase';

// Renders the common rich text component but applies alignment and spacing.
export const RichText: FC<IRichTextProps> = ({
  contentLength = 'short',
  'data-testid': testId,
  alignment,
  spacing = 'medium',
  hasMargin,
  hasPadding,
  dictionary = {},
  id,
  contentId,
  ...otherProps
}: IRichTextProps) => (
  <ContentFormatter
    id={id}
    data-testid={testId}
    data-contentid={contentId}
    alignment={alignment}
    spacing={spacing}
    hasMargin={hasMargin}
    hasPadding={hasPadding}
    contentLength={contentLength}
  >
    <Dictionary value={dictionary}>
      <RichTextBase {...otherProps} />
    </Dictionary>
  </ContentFormatter>
);

RichText.displayName = 'RichText';
